<!-- STEP 1 — conditions + related operations -->
<div class="configure-conditions" *ngIf="step === 'edit'">
  <aside class="side-panel">
    <div class="side-header">
      <h1>Configurar condiciones</h1>
      <button type="button" class="close-button" (click)="close()" aria-label="Cerrar">&times;</button>
    </div>

    <p class="side-subtitle">{{ providerLabel }}: <strong>{{ providerName | uppercase }}</strong></p>
    <p class="side-subtitle muted">{{ operationType }} · {{ invoice?.currency | uppercase }}</p>

    <form [formGroup]="form" class="conditions-form">
      <div class="condition-field">
        <app-input formControlName="tdmPercentage" autocomplete="off" type="number" label="Costo mensual"></app-input>
        <span class="suffix">%</span>
        <p class="previous">Antes {{ previousTdm || '---' }} %</p>
      </div>

      <div class="condition-field">
        <app-input formControlName="reservationPercentage" autocomplete="off" type="number" label="Garantía"></app-input>
        <span class="suffix">%</span>
        <p class="previous">Antes {{ previousReservation || '---' }} %</p>
      </div>
    </form>

    <div class="side-message" *ngIf="!isCalculating && originIsBlocked">
      <span class="icon error">&#9888;</span>
      <p>
        El colchón de esta operación es de {{ originBackupDays }} días y
        {{ blockLimitText(originBackupDays) }}. Ajusta el costo mensual o la garantía para continuar.
      </p>
    </div>

    <div class="side-buttons">
      <fs-ui-button
        (click)="continueToReview()"
        [isDisabled]="!canContinue"
        [label]="'Continuar'"
      ></fs-ui-button>
      <fs-ui-button
        class="cancel-button"
        (click)="close()"
        [label]="'Cancelar'"
        [type]="'outline'"
      ></fs-ui-button>
    </div>
  </aside>

  <section class="main-panel">
    <div class="card origin-card">
      <p class="card-title">OPERACIÓN QUE ESTÁS CONFIGURANDO</p>

      <div class="origin-row">
        <div class="origin-identity">
          <p class="company">{{ counterpartyName(invoice) | uppercase }}</p>
          <p class="meta">
            {{ getCodes(invoice) }} · vence {{ invoice?.paymentDate | formatDate }} ·
            {{ getDuration(invoice) }} días
          </p>
        </div>

        <div class="origin-figure">
          <p class="label">Monto neto</p>
          <p class="net-amount">{{ amount(invoice, invoice?.netAmount | numberSpacing) }}</p>
        </div>

        <div class="origin-figure">
          <p class="label">Colchón de días</p>
          <p class="cushion" *ngIf="originCalculating">
            <span class="spinner"></span> Calculando…
          </p>
          <p class="cushion" *ngIf="!originCalculating">
            <span
              class="cushion-value"
              *ngIf="isNumber(originBackupDays); else noOriginCushion"
              [ngClass]="getEvaluationClass(originBackupDays)"
            >
              <img
                *ngIf="isHealthyEvaluation(originBackupDays)"
                src="../../../../../../assets/icons/green-check-circle.svg"
                width="15px"
                height="15px"
                aria-hidden="true"
              />
              {{ originBackupDays }} d
            </span>
            <ng-template #noOriginCushion><span class="muted">---</span></ng-template>
          </p>
        </div>
      </div>
    </div>

    <div class="list-header">
      <p class="card-title inline">SUMA MÁS OPERACIONES</p>
      <span class="in-batch" *ngIf="selectedOperations.length > 0">
        {{ selectedOperations.length }} en el lote
      </span>
    </div>
    <p class="list-subtitle">
      Aprobadas, del mismo {{ providerLabel | lowercase }} y en la misma moneda. El colchón se calcula al
      seleccionar.
    </p>

    <div class="code-table-wrap">
      <div class="code-table" *ngIf="operations?.length > 0; else emptyOperations">
        <table>
          <tr *ngFor="let operation of operations" [ngClass]="{ blocked: operation.blocked }">
            <td class="col-check">
              <app-checkbox
                (selectionChanged)="setSelection(operation)"
                [checked]="operation.selected"
                [disabled]="operation.blocked || operation.calculating || !form.valid"
                content=""
              ></app-checkbox>
            </td>

            <td class="col-company">
              <p class="company">{{ counterpartyName(operation) | uppercase }}</p>
              <p class="meta">
                {{ operation?.paymentDate | formatDate }} · {{ getDuration(operation) }} días ·
                {{ getCodes(operation) }}
              </p>

              <p class="operation-error" *ngIf="operation.blocked">
                Su colchón es de {{ backupDays(operation) }} días y
                {{ blockLimitText(backupDays(operation)) }}, así que no entra al lote. Ajusta el costo
                mensual o la garantía si quieres incluirla.
              </p>
            </td>

            <td class="col-amount">
              <p class="label">Monto neto</p>
              <p class="value">{{ amount(operation, operation?.netAmount | numberSpacing) }}</p>
            </td>

            <td class="col-amount col-price">
              <ng-container *ngIf="operation.cost?.salePriceAmount">
                <p class="label">Precio total</p>
                <p class="value">{{ amount(operation, operation.cost.salePriceAmount | numberSpacing) }}</p>
              </ng-container>
            </td>

            <td class="col-cushion">
              <ng-container *ngIf="operation.calculating">
                <span class="spinner"></span> Calculando…
              </ng-container>

              <ng-container *ngIf="!operation.calculating && isNumber(backupDays(operation))">
                <span [ngClass]="getEvaluationClass(backupDays(operation))">{{ backupDays(operation) }} d</span>
              </ng-container>

              <ng-container *ngIf="!operation.calculating && !isNumber(backupDays(operation))">
                <span class="muted">Colchón al seleccionar</span>
              </ng-container>
            </td>
          </tr>
        </table>
      </div>

      <ng-template #emptyOperations>
        <p class="empty">No hay operaciones relacionadas.</p>
      </ng-template>
    </div>
  </section>
</div>

<!-- STEP 2 — review before sending -->
<div class="review" *ngIf="step === 'review'">
  <h1>Revisa antes de enviar</h1>
  <p class="review-subtitle">
    Vas a configurar {{ batch.length }} {{ batch.length === 1 ? 'operación' : 'operaciones' }} de
    {{ providerName | uppercase }}.
  </p>

  <p class="section-title">CONDICIONES QUE SE APLICAN</p>

  <div class="conditions-summary">
    <div class="condition-summary">
      <p class="label">Costo mensual</p>
      <p class="value">{{ conditions.tdmPercentage }} %</p>
      <p class="previous">antes {{ previousTdm || '---' }} %</p>
    </div>

    <div class="condition-summary">
      <p class="label">Garantía</p>
      <p class="value">{{ conditions.reservationPercentage }} %</p>
      <p class="previous">antes {{ previousReservation || '---' }} %</p>
    </div>
  </div>

  <div class="review-divider"></div>

  <p class="section-title">OPERACIONES ({{ batch.length }})</p>
  <p class="list-subtitle">Cada operación se acepta y se financia por separado.</p>

  <div class="review-operation" *ngFor="let operation of batch">
    <div class="review-operation-header">
      <div>
        <p class="company">
          {{ counterpartyName(operation) | uppercase }}
          <span class="origin-badge" *ngIf="operation.isOrigin">origen</span>
        </p>
        <p class="meta">
          {{ getCodes(operation) }} · vence {{ operation?.paymentDate | formatDate }} ·
          {{ getDuration(operation) }} días
        </p>
      </div>

      <div class="review-net">
        <p class="label">Monto neto</p>
        <p class="net-amount">{{ amount(operation, operation?.netAmount | numberSpacing) }}</p>
      </div>
    </div>

    <div class="review-breakdown">
      <div class="breakdown-item">
        <p class="label">Costo sin IGV</p>
        <p class="value">{{ (operation.cost?.costWithoutIGV | numberSpacing) || '---' }}</p>
      </div>
      <div class="breakdown-item">
        <p class="label">Costo de financiamiento</p>
        <p class="value">{{ (operation.cost?.investorsReturnAmount | numberSpacing) || '---' }}</p>
      </div>
      <div class="breakdown-item">
        <p class="label">Comisión Finsmart</p>
        <p class="value">{{ (operation.cost?.finsmartRevenueAmount | numberSpacing) || '---' }}</p>
      </div>
      <div class="breakdown-item">
        <p class="label">IGV sobre comisión</p>
        <p class="value">{{ (operation.cost?.finsmartRevenueTaxAmount | numberSpacing) || '---' }}</p>
      </div>
      <div class="breakdown-item">
        <p class="label">Precio total</p>
        <p class="value">{{ (operation.cost?.salePriceAmount | numberSpacing) || '---' }}</p>
      </div>
      <div class="breakdown-item">
        <p class="label">Garantía retenida</p>
        <p class="value">{{ (operation.cost?.reservationAmount | numberSpacing) || '---' }}</p>
      </div>
      <div class="breakdown-item">
        <p class="label">Colchón de días</p>
        <p class="value">
          <span
            *ngIf="isNumber(operation.cost?.evaluationDays); else noReviewCushion"
            [ngClass]="getEvaluationClass(operation.cost?.evaluationDays)"
          >
            {{ operation.cost?.evaluationDays }} d
          </span>
          <ng-template #noReviewCushion>---</ng-template>
        </p>
      </div>
    </div>
  </div>

  <div class="review-disclaimer">
    <span class="icon info">&#9432;</span>
    <p>
      Al confirmar le enviamos a {{ providerName | uppercase }}
      {{ batch.length === 1 ? 'un correo' : 'un correo por cada una de las ' + batch.length + ' operaciones' }},
      para que acepte las condiciones.
    </p>
  </div>

  <p class="submit-error" *ngIf="submitError">{{ submitError }}</p>

  <div class="review-buttons">
    <fs-ui-button
      (click)="backToEdit()"
      [isDisabled]="submitting"
      [label]="'Volver a editar'"
      [type]="'secondary'"
    ></fs-ui-button>
    <fs-ui-button
      (click)="confirm()"
      [isDisabled]="submitting"
      [label]="submitting ? 'Enviando…' : batch.length === 1 ? 'Confirmar y enviar correo' : 'Confirmar y enviar correos'"
    ></fs-ui-button>
  </div>
</div>

<!-- STEP 3 — done -->
<div class="success" *ngIf="step === 'success'">
  <img
    class="success-check"
    src="../../../../../../assets/icons/big-check-mark-icon.svg"
    aria-hidden="true"
  />

  <h1>Factura(s) configuradas correctamente</h1>

  <p class="success-subtitle">
    Le enviamos {{ batch.length === 1 ? 'un correo' : 'un correo por operación' }} al
    cliente para que acepte las condiciones.
  </p>

  <div class="success-buttons">
    <fs-ui-button (click)="finish()" [label]="'Regresar'"></fs-ui-button>
  </div>
</div>
